import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { ChevronRight, Menu } from "Shared/appIcons";
import type { Breadcrumb } from "Shared/Layouts/components/types";
import { UnstyledLink } from "Shared/Routing/UnstyledLink";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";

type Props = {
	title: string;
	subtitle?: string;
	breadcrumbs?: Breadcrumb[];
	actions?: ReactNode;
	clientSwitcher?: ReactNode;
	onOpenMenu: () => void;
};

/**
 * Hamburger trigger for `DrawerNav` plus the current page header (title,
 * breadcrumbs, actions). Visible only below the `md` breakpoint — on wider
 * viewports `TopbarNav` + `PageHeader` cover the same surface.
 */
export const DrawerTopbar = ({
	title,
	subtitle,
	breadcrumbs,
	actions,
	clientSwitcher,
	onOpenMenu,
}: Props) => {
	const { t } = useResource();
	const hasBreadcrumbs = !!breadcrumbs && breadcrumbs.length > 0;

	return (
		<header className="border-border bg-card flex min-h-14 flex-shrink-0 items-center border-b px-4 py-2 md:hidden">
			<Button
				variant="ghost"
				size="icon-sm"
				onClick={onOpenMenu}
				className="text-muted-foreground hover:text-foreground -ml-1 mr-2"
				title={t(Resources.Sidebar.Expand)}
			>
				<Menu className="h-4 w-4" />
			</Button>
			<div className="flex min-w-0 flex-1 items-center">
				{hasBreadcrumbs ? (
					<nav className="mr-3 flex items-center gap-1.5">
						{breadcrumbs.flatMap((crumb, idx) => {
							const key = `${crumb.to ?? ""}::${crumb.label}`;
							const node = crumb.to ? (
								<UnstyledLink
									key={key}
									to={crumb.to}
									className="text-small text-muted-foreground hover:text-foreground no-underline"
								>
									{crumb.label}
								</UnstyledLink>
							) : (
								<span key={key} className="text-small text-muted-foreground">
									{crumb.label}
								</span>
							);
							if (idx === 0) return [node];
							return [
								<ChevronRight
									key={`sep-${key}`}
									className="text-muted-foreground h-3.5 w-3.5"
									strokeWidth={2}
								/>,
								node,
							];
						})}
					</nav>
				) : null}
				<div className="min-w-0">
					<h1 className="text-h2 text-foreground truncate font-semibold">
						{title}
					</h1>
					{subtitle ? (
						<div className="text-tiny text-muted-foreground mt-0.5 truncate">
							{subtitle}
						</div>
					) : null}
					{clientSwitcher ? (
						<div className="mt-1.5">{clientSwitcher}</div>
					) : null}
				</div>
			</div>
			{actions ? (
				<div className="ml-2 flex flex-shrink-0 items-center gap-1">
					{actions}
				</div>
			) : null}
		</header>
	);
};
